/**
 * Who joined the chat and who left. One person on a side gets the video's big
 * treatment (the avatar rolls in, the name spells itself out); several get a
 * compact list; nobody gets "Nobody." slammed in.
 */

import { Avatar, Fit, Letters, Scene, Zone, type BlobSpec } from "../../story/stage";
import { useTimeline } from "../../story/timeline";
import type { Chat } from "../../types";
import { shortDate } from "../shared";

type Join = Chat["joins"][number];

const BLOBS: BlobSpec[] = [
  { colour: "yellow", x: 1010, y: 1780, r: 260, seed: 29.5, wob: 0.2 },
  { colour: "blurple", x: 40, y: 1000, r: 90, seed: 30.1, wob: 0.24 },
];

const FIRST = 320;
/** From a side's label to what's under it. */
const BODY = 60;
const BIG = 340;
const NOBODY = 234;
const ROW = 120;
const LABEL = "color-mix(in srgb, var(--wr-ink) 75%, transparent)";

type Side = {
  kind: Join["kind"];
  title: string;
  people: Join[];
  shown: Join[];
  more: number;
  shape: "nobody" | "big" | "list";
  top: number;
  height: number;
};

/** Stack the two sides down the card, giving a long list more rows when the other side is short. */
function layout(joins: Join[]): Side[] {
  const sides = [
    { kind: "joined" as const, title: "Joined", people: joins.filter((j) => j.kind === "joined") },
    { kind: "left" as const, title: "Left", people: joins.filter((j) => j.kind === "left") },
  ];
  const lists = sides.filter((s) => s.people.length > 1).length;
  const cap = lists === 2 ? 4 : 6;
  const gap = lists ? 120 : 180;
  let top = FIRST;
  return sides.map((side) => {
    const n = side.people.length;
    const shape = n === 0 ? "nobody" : n === 1 ? "big" : "list";
    const shown = n > cap ? side.people.slice(0, cap - 1) : side.people;
    const more = n - shown.length;
    const height = shape === "nobody" ? NOBODY : shape === "big" ? BIG : (shown.length + (more ? 1 : 0)) * ROW - 24;
    const placed: Side = { ...side, shown, more, shape, top, height };
    top += BODY + height + gap;
    return placed;
  });
}

function SideView({ side }: { side: Side }) {
  const body = side.top + BODY;
  const verb = side.kind === "joined" ? "Joined" : "Left";
  return (
    <div data-side={side.kind}>
      <div data-part="label" className="kick" style={{ top: side.top, color: LABEL }}>
        {side.title}
      </div>
      {side.shape === "nobody" ? (
        <div data-part="nobody" className="abs disp" style={{ left: 60, top: body, fontSize: 260, transformOrigin: "0 50%" }}>
          Nobody.
        </div>
      ) : null}
      {side.shape === "big" ? (
        <>
          <Avatar name={side.shown[0].name} size={240} style={{ left: 72, top: body }} />
          <Fit size={150} min={60} className="abs disp j-name" style={{ left: 350, right: 60, top: body + 50 }}>
            <Letters text={side.shown[0].name} />
          </Fit>
          <div data-part="sub" className="line" style={{ top: body + 280 }}>
            {verb} on {shortDate(side.shown[0].at)}.
          </div>
        </>
      ) : null}
      {side.shape === "list" ? (
        <>
          {side.shown.map((j, i) => (
            <div
              key={`${j.at}-${i}`}
              data-testid={`${side.kind}-row`}
              className="abs j-row"
              style={{ left: 72, right: 72, top: body + i * ROW, height: 96, display: "flex", alignItems: "center", gap: 28 }}
            >
              <Avatar name={j.name} size={96} inline style={{ borderWidth: 6 }} />
              <Fit size={80} min={36} className="disp j-text" style={{ flex: 1, minWidth: 0 }}>
                {j.name}
              </Fit>
              <div className="mono j-text" style={{ fontSize: 30, fontWeight: 700, letterSpacing: "0.04em" }}>
                {shortDate(j.at).toUpperCase()}
              </div>
            </div>
          ))}
          {side.more ? (
            <div
              className="abs mono j-more"
              style={{ left: 72, top: body + side.shown.length * ROW, height: 96, display: "flex", alignItems: "center", fontSize: 34, fontWeight: 700 }}
            >
              +{side.more} MORE
            </div>
          ) : null}
        </>
      ) : null}
    </div>
  );
}

export function Joins({ joins }: { joins: Chat["joins"] }) {
  const sides = layout(joins);
  const [joined, left] = sides;
  const closing = !left.people.length
    ? "And everyone stayed."
    : joined.people.length
      ? `${joined.people.length} in, ${left.people.length} out.`
      : null;
  const last = sides[1];

  const root = useTimeline((s) => {
    s.rise("[data-part=kick]", 0.3, { y: 0 });
    let at = 0.4;
    let done = at;
    for (const side of sides) {
      const scope = `[data-side=${side.kind}]`;
      s.rise(`${scope} [data-part=label]`, at, { y: 0, sound: false });
      if (side.shape === "nobody") {
        s.slam(`${scope} [data-part=nobody]`, at + 0.2, { from: 2.6 });
        s.shake(s.q(".layer")[0], at + 0.5, 7, 18);
        done = at + 0.7;
        at += 1.0;
      } else if (side.shape === "big") {
        s.tl.fromTo(
          `${scope} .av`,
          { x: 900, rotation: 180, autoAlpha: 0 },
          { x: 0, rotation: 0, autoAlpha: 1, duration: 0.7, ease: "back.out(1.4)" },
          at + 0.1,
        );
        s.sound("whoosh", at + 0.1);
        s.letters(`${scope} .j-name`, at + 0.4, { y: 100 });
        s.rise(`${scope} [data-part=sub]`, at + 0.8, { y: 24, sound: false });
        done = at + 1.3;
        at += 1.4;
      } else {
        // The list rolls in one row at a time, each avatar ahead of its name.
        s.q(`${scope} .j-row`).forEach((row, i) => {
          const t = at + 0.1 + i * 0.16;
          s.tl.fromTo(row.querySelector(".av"), { x: 600, rotation: 180, autoAlpha: 0 }, { x: 0, rotation: 0, autoAlpha: 1, duration: 0.6, ease: "back.out(1.4)" }, t);
          s.rise(row.querySelectorAll(".j-text"), t + 0.25, { y: 0, x: 60, sound: false });
          s.sound("pop", t + 0.25, 1 + i * 0.06);
        });
        const rows = side.shown.length + (side.more ? 1 : 0);
        if (side.more) s.rise(`${scope} .j-more`, at + 0.1 + side.shown.length * 0.16 + 0.25, { y: 24, sound: false });
        done = at + rows * 0.16 + 0.8;
        at = done + 0.2;
      }
    }
    if (closing) s.rise("[data-part=close]", done, { y: 24 });
  });

  return (
    <div ref={root} className="contents">
      <Scene blobs={BLOBS}>
        <Zone at="top">
          <div data-part="kick" className="kick">
            Who joined and who left
          </div>
        </Zone>
        {/* Joined keeps to the upper part and Left to the lower, so a taller screen opens room around both. */}
        <Zone at={0.25}>
          <SideView side={joined} />
        </Zone>
        <Zone at={0.75}>
          <SideView side={left} />
          {closing ? (
            <div data-part="close" className="line" style={{ top: last.top + BODY + last.height + 36 }}>
              {closing}
            </div>
          ) : null}
        </Zone>
      </Scene>
    </div>
  );
}
